/**
 * HTML Sanitization Utility
 *
 * SECURITY: This utility provides HTML sanitization to prevent XSS attacks
 * when rendering user-generated or external HTML content.
 *
 * TODO: Install DOMPurify for production-grade sanitization
 * Run: npm install dompurify @types/dompurify
 *
 * Once installed, uncomment the DOMPurify import and use it instead of the fallback.
 */

// Uncomment when DOMPurify is installed:
// import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  // TODO: Replace this with DOMPurify once installed
  // return DOMPurify.sanitize(html, {
  //   ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'img', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
  //   ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height'],
  //   ALLOW_DATA_ATTR: false,
  // });

  // FALLBACK: Basic sanitization (NOT PRODUCTION READY - INSTALL DOMPURIFY!)
  // This is a minimal fallback that removes script tags and event handlers
  // It is NOT a complete XSS protection solution
  console.warn('⚠️ SECURITY WARNING: Using fallback HTML sanitization. Install DOMPurify for production!');

  return html
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove inline event handlers (onclick, onerror, etc.)
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: protocol in href/src
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:text\/html/gi, 'data-blocked:text/html');
};

/**
 * Sanitizes HTML for email content specifically
 * Allows more tags and attributes than the general sanitizer
 *
 * @param html - The email HTML to sanitize
 * @returns Sanitized HTML string
 */
export const sanitizeEmailHtml = (html: string | null | undefined): string => {
  if (!html) {
    return '';
  }

  // TODO: Replace with DOMPurify configuration for emails
  // return DOMPurify.sanitize(html, {
  //   ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'img', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'blockquote', 'pre', 'code', 'hr', 'b', 'i'],
  //   ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'width', 'height', 'align', 'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan'],
  //   ALLOW_DATA_ATTR: false,
  //   ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  // });

  // Use the basic sanitizer as fallback
  return sanitizeHtml(html);
};
